import React, { useEffect, useState, useRef, useMemo } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Polyline } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix Leaflet's default icon paths
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

const driverIcon = new L.Icon({
    iconUrl: 'https://cdn-icons-png.flaticon.com/512/3063/3063822.png',
    iconSize: [40, 40],
    iconAnchor: [20, 20],
});

const storeIcon = new L.Icon({
    iconUrl: 'https://cdn-icons-png.flaticon.com/512/3081/3081840.png',
    iconSize: [40, 40],
    iconAnchor: [20, 40],
});

const customerIcon = new L.Icon({
    iconUrl: 'https://cdn-icons-png.flaticon.com/512/1904/1904428.png',
    iconSize: [40, 40],
    iconAnchor: [20, 40],
});

const DriverMapRoute = ({ storeLocation, customerAddress, customerLocation, driverLocation }) => {
    const mapRef = useRef(null);
    const [route, setRoute] = useState([]);
    const [resolvedCustomerLoc, setResolvedCustomerLoc] = useState(customerLocation);

    // Geocode customer address if location is missing
    useEffect(() => {
        if (resolvedCustomerLoc || !customerAddress) return;
        
        const geocode = async () => {
            try {
                const res = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(customerAddress)}`);
                const data = await res.json();
                if (data && data.length > 0) {
                    setResolvedCustomerLoc({ lat: parseFloat(data[0].lat), lng: parseFloat(data[0].lon) });
                }
            } catch (err) {
                console.error("Geocoding failed", err);
            }
        };
        geocode();
    }, [customerAddress, resolvedCustomerLoc]);

    // Optional: fetch a route from OSRM between store and customer. 
    useEffect(() => {
        if (!storeLocation || !resolvedCustomerLoc) return;
        
        const fetchRoute = async () => {
            try {
                // Using OSRM public API (Note: in production, use a paid service or host your own)
                const response = await fetch(`https://router.project-osrm.org/route/v1/driving/${storeLocation.lng},${storeLocation.lat};${resolvedCustomerLoc.lng},${resolvedCustomerLoc.lat}?overview=full&geometries=geojson`);
                const data = await response.json();
                if (data && data.routes && data.routes[0]) {
                    const coordinates = data.routes[0].geometry.coordinates.map(coord => [coord[1], coord[0]]); // Leaflet uses [lat, lng]
                    setRoute(coordinates);
                }
            } catch (err) {
                console.error("Failed to fetch route:", err);
            }
        };

        fetchRoute();
    }, [storeLocation, resolvedCustomerLoc]);

    useEffect(() => {
        if (mapRef.current && driverLocation) {
             mapRef.current.setView([driverLocation.lat, driverLocation.lng], 15);
        }
    }, [driverLocation]);

    // Center map on driver if available, else store, else default (e.g. some central point)
    const center = driverLocation ? [driverLocation.lat, driverLocation.lng] : 
                   storeLocation ? [storeLocation.lat, storeLocation.lng] : 
                   [19.0760, 72.8777]; // Default Mumbai

    return (
        <div style={{ height: '300px', width: '100%', borderRadius: '12px', overflow: 'hidden' }}>
            <MapContainer center={center} zoom={13} style={{ height: '100%', width: '100%' }} ref={mapRef}>
                <TileLayer
                    url="https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png"
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>'
                />
                
                {storeLocation && (
                    <Marker position={[storeLocation.lat, storeLocation.lng]} icon={storeIcon}>
                        <Popup>QwickMed Store</Popup>
                    </Marker>
                )}

                {resolvedCustomerLoc && (
                    <Marker position={[resolvedCustomerLoc.lat, resolvedCustomerLoc.lng]} icon={customerIcon}>
                        <Popup>Customer Delivery Location</Popup>
                    </Marker>
                )}

                {driverLocation && (
                    <Marker position={[driverLocation.lat, driverLocation.lng]} icon={driverIcon}>
                        <Popup>Driver</Popup>
                    </Marker>
                )}

                {route.length > 0 && (
                    <Polyline positions={route} color="#0ea5e9" weight={5} opacity={0.7} />
                )}
            </MapContainer>
        </div>
    );
};

export default DriverMapRoute;
